import React, { useState } from 'react';
import { createCheckout } from '../api/subscriptions';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';

const PLANS = [
  {
    id: 'free',
    name: '無料',
    price_jpy: 0,
    description: '投票・コメントは無制限で無料',
    features: [
      { text: '局面の投稿：月5件まで', sub: null },
      { text: '投票・コメント：無制限', sub: null },
    ],
    cta: null,
  },
  {
    id: 'standard',
    name: 'プレミアム',
    price_jpy: 480,
    description: 'たくさん投稿したい方へ',
    features: [
      { text: '局面の投稿：無制限', sub: '月5件の制限を解除' },
    ],
    cta: 'プレミアムにする',
    highlight: true,
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);

  const handleCheckout = async (tier: string) => {
    if (!user) {
      alert('ご購入にはログインが必要です');
      return;
    }
    setLoadingTier(tier);
    try {
      const { checkout_url } = await createCheckout(tier);
      window.location.href = checkout_url;
    } catch (err: any) {
      alert(err.message ?? '決済ページを開けませんでした');
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 space-y-10">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">プランを選択</h1>
        <p className="text-gray-500">みんなで何切るをもっと深く楽しもう</p>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {PLANS.map((plan) => {
          const isPremium = plan.id === 'standard';
          const isCurrentPlan = user?.subscription_tier === plan.id ||
            (!user && plan.id === 'free');

          return (
            <div
              key={plan.id}
              className={`border-2 rounded-2xl p-6 space-y-5
                ${isPremium ? 'border-green-400 bg-gradient-to-b from-green-50 to-white shadow-lg' : 'border-gray-200 bg-white'}
                ${isCurrentPlan ? 'ring-2 ring-blue-400' : ''}
              `}
            >
              {isPremium && (
                <div className="text-center -mt-1">
                  <span className="inline-block bg-green-500 text-white text-xs font-bold px-3 py-0.5 rounded-full">
                    おすすめ
                  </span>
                </div>
              )}

              <div>
                <h2 className="text-xl font-bold text-gray-900">{plan.name}</h2>
                <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                <div className="mt-3">
                  {plan.price_jpy === 0 ? (
                    <span className="text-3xl font-bold text-gray-900">無料</span>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-gray-900">
                        ¥{plan.price_jpy.toLocaleString()}
                      </span>
                      <span className="text-gray-500 text-sm ml-1">/月</span>
                    </>
                  )}
                </div>
              </div>

              <ul className="space-y-3">
                {plan.features.map((f) => (
                  <li key={f.text} className="flex items-start gap-2 text-sm">
                    <span className={`mt-0.5 shrink-0 ${isPremium ? 'text-green-500' : 'text-gray-400'}`}>✓</span>
                    <span>
                      <span className="text-gray-700">{f.text}</span>
                      {f.sub && (
                        <span className="block text-xs text-gray-400 mt-0.5">{f.sub}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {plan.cta ? (
                <Button
                  onClick={() => handleCheckout(plan.id)}
                  loading={loadingTier === plan.id}
                  disabled={isCurrentPlan}
                  variant={isPremium ? 'primary' : 'secondary'}
                  className="w-full"
                >
                  {isCurrentPlan ? '現在のプラン' : plan.cta}
                </Button>
              ) : (
                <div className="text-center text-sm text-gray-500 pt-1">
                  {isCurrentPlan ? '現在のプラン' : '登録不要で利用開始'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto space-y-3">
        <h2 className="text-lg font-bold text-gray-900">よくある質問</h2>
        {[
          {
            q: '解約はできますか？',
            a: 'いつでも解約可能です。解約後も月末まではプレミアム機能が利用できます。',
          },
          {
            q: '無料プランでも投票・コメントはできますか？',
            a: 'はい。投票・コメントは無制限で無料です。局面の投稿のみ月5件の上限があります。',
          },
        ].map(({ q, a }) => (
          <details key={q} className="border rounded-xl p-4 bg-white">
            <summary className="font-medium text-sm cursor-pointer">{q}</summary>
            <p className="mt-2 text-sm text-gray-600">{a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}
